import { useState } from 'react';
import type { CheckoutPlatform, PaymentOptions } from '../api/marketplace';
import { useCart } from '../features/cart/CartContext';
import { PaymentSheet } from '../features/checkout/PaymentSheet';
import { notifyPayResult, payErrorMessage, payItems } from '../features/checkout/pay';
import { StarIcon } from '../features/tabs/icons';
import { haptic, hapticImpact } from '../lib/telegram';
import './CartSheet.css';

interface CartSheetProps {
  payments: PaymentOptions;
  onPurchased: (orderId?: string) => void;
}

export function CartSheet({ payments, onPurchased }: CartSheetProps) {
  const cart = useCart();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);

  const pay = async (platform: CheckoutPlatform): Promise<void> => {
    if (cart.lines.length === 0) return;
    setBusy(true);
    setError(null);

    try {
      const outcome = await payItems(
        cart.lines.map((line) => ({
          productId: line.productId,
          quantity: line.quantity,
          ...(line.editionId ? { editionId: line.editionId } : {}),
        })),
        platform,
      );
      const message = notifyPayResult(outcome.result);
      if (message) setError(message);
      if (outcome.result === 'paid') {
        cart.clear();
        onPurchased(outcome.orderId);
      } else if (outcome.result === 'pending') {
        onPurchased();
      }
    } catch (cause) {
      haptic('error');
      setError(payErrorMessage(cause));
    } finally {
      setBusy(false);
      setPayOpen(false);
    }
  };

  return (
    <div className="cart">
      <h1 className="page-title">Корзина</h1>

      {cart.lines.length === 0 ? (
        <p className="cart__empty">Пока пусто. Добавь товар с карточки</p>
      ) : (
        <>
          <ul className="cart__list">
            {cart.lines.map((line) => (
              <li key={`${line.productId}:${line.editionId ?? ''}`} className="cart__line">
                <div>
                  <p className="cart__name">{line.title}</p>
                  <p className="cart__price">
                    <StarIcon size={12} /> {line.priceStars}
                    {line.priceRub ? ` · ${line.priceRub} ₽` : ''} × {line.quantity}
                  </p>
                </div>
                <div className="cart__qty">
                  <button
                    type="button"
                    onClick={() => {
                      hapticImpact('light');
                      cart.setQuantity(line.productId, line.quantity - 1, line.editionId);
                    }}
                  >
                    −
                  </button>
                  <span>{line.quantity}</span>
                  <button
                    type="button"
                    onClick={() => {
                      hapticImpact('light');
                      cart.setQuantity(line.productId, Math.min(line.maxPerOrder, line.quantity + 1), line.editionId);
                    }}
                  >
                    +
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {error ? <p className="cart__error">{error}</p> : null}

          <p className="cart__total">
            Итого <StarIcon /> {cart.totalStars}
            {cart.totalRub > 0 ? ` · ${cart.totalRub} ₽` : ''}
          </p>

          <button type="button" className="cart__buy" disabled={busy} onClick={() => setPayOpen(true)}>
            Приобрести
          </button>
        </>
      )}

      <PaymentSheet
        open={payOpen}
        payments={{ ...payments, tribute: payments.tribute || cart.lines.some((line) => line.hasTribute) }}
        busy={busy}
        starsAmount={cart.totalStars}
        rubAmount={cart.totalRub || undefined}
        onClose={() => setPayOpen(false)}
        onPay={(platform) => void pay(platform)}
      />
    </div>
  );
}
